import { useEffect, useRef, useState } from 'react';
import { notify } from '../../../utils/toast';
import { formatRecordingTime } from '../lib/audioRecorder';
import {
    AudioRecordingSession,
    type AudioRecordingSnapshot,
} from '../lib/audioRecordingSession';

export type UseAudioRecorderReturn = AudioRecordingSnapshot & {
    startRecording: () => Promise<void>;
    stopRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
    togglePreviewPlayback: () => void;
    seekPreview: (ratio: number) => void;
    cancelRecording: () => void;
    formatTime: (seconds: number) => string;
};

const INITIAL: AudioRecordingSnapshot = {
    isRecording: false,
    isPaused: false,
    isPreviewPlaying: false,
    canResumePreview: false,
    previewProgress: 0,
    recordingTime: 0,
};

/** Ponte React fina sobre {@link AudioRecordingSession}. */
export function useAudioRecorder(onAudioReady: (file: File) => void): UseAudioRecorderReturn {
    const [snap, setSnap] = useState<AudioRecordingSnapshot>(INITIAL);
    const sessionRef = useRef<AudioRecordingSession | null>(null);
    const onReadyRef = useRef(onAudioReady);
    onReadyRef.current = onAudioReady;

    useEffect(() => {
        const session = new AudioRecordingSession({
            onReady: (file) => onReadyRef.current(file),
            onError: (message) => notify.error(message),
            onChange: setSnap,
        });
        sessionRef.current = session;
        setSnap(session.getSnapshot());
        return () => {
            session.dispose();
            sessionRef.current = null;
        };
    }, []);

    return {
        ...snap,
        startRecording: () => sessionRef.current?.start() ?? Promise.resolve(),
        stopRecording: () => sessionRef.current?.stop(),
        pauseRecording: () => sessionRef.current?.pause(),
        resumeRecording: () => sessionRef.current?.resume(),
        togglePreviewPlayback: () => sessionRef.current?.togglePreview(),
        seekPreview: (ratio) => sessionRef.current?.seekPreview(ratio),
        cancelRecording: () => sessionRef.current?.cancel(),
        formatTime: formatRecordingTime,
    };
}
